import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { useEngine } from '@/lib/motion/section'
import { clamp, smoothstep } from '@/lib/motion/math'
import { Bracket, createStyleCache } from '@/components/landing/primitives'
import { hero } from '@/constants/content'

const PANEL_BACKGROUND = [
    'radial-gradient(120% 85% at 50% -5%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 58%)',
    'radial-gradient(70% 60% at 88% 105%, rgba(190,212,222,0.6) 0%, rgba(190,212,222,0) 70%)',
    'radial-gradient(60% 55% at 6% 96%, rgba(205,222,229,0.55) 0%, rgba(205,222,229,0) 70%)',
    'linear-gradient(180deg, #eef3f5 0%, #e4ebee 100%)'
].join(', ')

const Crosshair: FC<{ className: string }> = ({ className }): ReactNode => {
    return (
        <svg viewBox='0 0 16 16' aria-hidden='true' className={className}>
            <path d='M8 0v16M0 8h16' stroke='#b9c3c8' strokeWidth='1' />
        </svg>
    )
}

const DotCluster: FC = (): ReactNode => {
    return (
        <span aria-hidden='true' className='grid grid-cols-3 gap-[3px]'>
            {Array.from({ length: 9 }).map((_, index) => (
                <span
                    key={index}
                    className='dot-pulse size-[3px] rounded-full bg-ink'
                    style={{ animationDelay: `${((index % 3) + Math.floor(index / 3)) * 0.16}s` }}
                />
            ))}
        </span>
    )
}

const HeroSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    const wordmarkRef = useRef<HTMLDivElement>(null)
    const copyRef = useRef<HTMLDivElement>(null)
    const cueRef = useRef<HTMLDivElement>(null)
    const labelRef = useRef<HTMLDivElement>(null)

    useEngine(() => {
        const set = createStyleCache()
        let height = 1

        return {
            measure: () => {
                height = sectionRef.current?.offsetHeight ?? window.innerHeight
            },
            frame: (state) => {
                if (state.scroll > height * 1.2) return

                const p = clamp(state.scroll / Math.max(1, height))
                const collapse = smoothstep(0, 0.85, p)

                set(panelRef.current, 'clip-path', `inset(0% 0% ${(collapse * 100).toFixed(2)}% 0% round 28px)`)
                set(panelRef.current, 'transform', `translate3d(0, ${(p * height * 0.3).toFixed(1)}px, 0)`)

                set(wordmarkRef.current, 'transform', `translate3d(${(-state.mouseX * 16).toFixed(1)}px, ${(-p * height * 0.14 - state.mouseY * 7).toFixed(1)}px, 0)`)
                set(wordmarkRef.current, 'opacity', clamp(1 - p * 1.15).toFixed(3))

                set(copyRef.current, 'transform', `translate3d(0, ${(-p * 80).toFixed(1)}px, 0)`)
                set(copyRef.current, 'opacity', clamp(1 - p * 2.4).toFixed(3))
                set(cueRef.current, 'opacity', clamp(1 - p * 4).toFixed(3))
                set(labelRef.current, 'opacity', clamp(1 - p * 3).toFixed(3))
            }
        }
    })

    return (
        <section id='hero' ref={sectionRef} aria-labelledby='hero-title' className='relative h-svh min-h-[540px]'>
            <div
                ref={panelRef}
                className='absolute inset-x-2 bottom-2 top-16 overflow-hidden rounded-[28px] border border-white/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_1px_0_rgba(29,42,49,0.04)] will-change-transform md:inset-x-3 md:bottom-3'
                style={{ background: PANEL_BACKGROUND }}
            >
                <div ref={wordmarkRef} aria-hidden='true' className='absolute inset-0 flex items-center justify-center will-change-transform'>
                    <span className='ghost-wordmark select-none whitespace-nowrap text-[clamp(190px,40vw,780px)] font-light leading-none tracking-[-0.075em]'>
                        orvane
                    </span>
                </div>

                <Crosshair className='absolute left-5 top-5 size-4' />
                <Crosshair className='absolute bottom-5 right-5 hidden size-4 md:block' />

                <div ref={labelRef} className='absolute left-12 top-[22px] flex items-center gap-4 text-haze'>
                    <Bracket>Orvane L4</Bracket>
                    <span className='hidden h-px w-10 bg-pale md:block' />
                    <span className='label-mono hidden md:inline'>Self-flying survey unit</span>
                </div>

                <div ref={copyRef} className='absolute bottom-6 left-5 max-w-[680px] pr-12 md:bottom-10 md:left-10 md:pr-0'>
                    <h1 id='hero-title' className='text-[clamp(22px,2.7vw,42px)] font-light leading-[1.08] tracking-[-0.04em]'>
                        <span className='block text-ink'>{hero.lead}</span>
                        <span className='block text-haze'>{hero.follow}</span>
                    </h1>
                </div>

                <div ref={cueRef} className='absolute bottom-6 right-5 flex items-center gap-3 md:bottom-10 md:right-10'>
                    <span className='hidden text-[13px] tracking-[-0.01em] text-ink sm:inline'>Scroll Down</span>
                    <DotCluster />
                </div>
            </div>
        </section>
    )
}

export default HeroSection
